import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ContractSignature, PublicContractSignatureSession } from '@models/index';
import { EndpointParams } from '@models/utils/endpoint-params';

import { HttpService } from '@services/http.service';
import { InternationalizationService } from '@services/utils/internationalization/internationalization.service';

// La ceremonia: publica, sin sesion de usuario, identificada por el GUID de la URL.
//
// Dos cosas que se rompen en silencio si se omiten:
// - `language_code` en la query. Sin sesion el backend no puede deducir el idioma y cae a ingles,
//   asi que el socio veria los errores en ingles (mismo patron que `auth/list` o `members/exist`).
// - El token del captcha (reCAPTCHA v3) va en la QUERY (`?recaptcha=`), no en cabecera. Mandarlo
//   implica que se valida: el filtro se traga 5 fallos en silencio y a partir de ahi devuelve 400
//   para esa IP y esa accion. Un acierto pone el contador a cero.
//
// El token entra por parametro a proposito: quien lo pide es la pantalla, y asi este servicio no
// depende del proveedor de captcha.
@Injectable({
    providedIn: 'root'
})
export class PublicContractSignatureSessionService extends HttpService {
    private readonly internationalizationService = inject(InternationalizationService);

    private readonly defaultEndpointParams: EndpointParams = { app: 'payments', version: 1 };

    search(guid: string, recaptchaToken: string): Observable<PublicContractSignatureSession> {
        return this.get(`public/contract-signature-sessions/${guid}${this.buildQuery(recaptchaToken)}`, this.defaultEndpointParams).pipe(
            map((response) => new PublicContractSignatureSession().build(response))
        );
    }

    // `lineIndex` es la posicion en el carrito expandido que vino en el item: se manda tal cual.
    // Los campos obligatorios los decide `items[].fields` de ESE documento, no una regla global, y
    // lo que ya este capturado en la sesion puede ir a null.
    sign(guid: string, lineIndex: number, signature: ContractSignature, recaptchaToken: string): Observable<void> {
        return this.post(`public/contract-signature-sessions/${guid}/items/${lineIndex}/sign${this.buildQuery(recaptchaToken)}`, this.defaultEndpointParams, signature);
    }

    // Declinar deja la sesion TERMINAL: no se puede firmar nada mas ni comprar con ella, y caduca
    // en el acto. Para reintentar hay que abrir una sesion nueva. No se pide motivo a proposito.
    reject(guid: string, lineIndex: number, recaptchaToken: string): Observable<void> {
        return this.post(`public/contract-signature-sessions/${guid}/items/${lineIndex}/reject${this.buildQuery(recaptchaToken)}`, this.defaultEndpointParams, null);
    }

    private buildQuery(recaptchaToken: string): string {
        return `?language_code=${this.internationalizationService.getLanguage()}&recaptcha=${recaptchaToken}`;
    }
}
